import { defineComponent, reactive, ref, computed, toRef } from 'vue-demi';
import { a as useIdAttribute, u as useControlAttributes } from './useIdAttribute-3a339b79.js';
import './TimeFormat-a7f5565b.js';
import './dom-53c9635b.js';
import '@vueuse/core';
import { u as useProxyModelValue } from './useProxyModelValue-e18f251e.js';
import { u as useResponsive } from './useResponsive-491da8c6.js';
import '@onereach/styles/tailwind.config.json';
import { u as useValidationAttributes } from './useValidationAttributes-257954f3.js';
import { s as script$1 } from './OrError-331220bc.js';
import { s as script$2 } from './OrHint-262c8217.js';
import { s as script$4 } from './OrIconButton-5a508869.js';
import { s as script$3 } from './OrIcon-9aff520e.js';
import { s as script$5, a as InputBoxVariant } from './OrInputBox-2f33498c.js';
import { s as script$6 } from './OrLabel-4abbce6e.js';
import { s as script$7 } from './OrLoader-5e2f359b.js';
import { resolveComponent, openBlock, createElementBlock, normalizeClass, createBlock, withCtx, renderSlot, createTextVNode, toDisplayString, createCommentVNode, createVNode, createSlots, withDirectives, createElementVNode, mergeProps, vModelDynamic, Fragment, withModifiers, vShow } from 'vue';
import { s as styleInject } from './style-inject.es-4c6f2515.js';

const Input = [
// Layout
'layout-column',
// Spacing
'gap-xs'];
const InputControl = [
// Box
'flex-auto',
// Typography
'typography-inherit',
// Theme (placeholder)
'placeholder:theme-foreground-outline', 'dark:placeholder:theme-foreground-outline-dark',
// Autofill hack,
// disable (delay color change)
'autofill:transition-colors autofill:duration-[5000s]',
// override background color using box-shadow
'autofill:shadow-[10px_0_1000px_theme(backgroundColor.primary-opacity-0-08)_inset]', 'dark:autofill:shadow-[0_0_0px_1000px_theme(backgroundColor.primary-opacity-0-08-dark)_inset]'];

var InputType;
(function (InputType) {
  InputType["Text"] = "text";
  InputType["Number"] = "number";
  InputType["Password"] = "password";
  InputType["Search"] = "search";
})(InputType || (InputType = {}));

var script = defineComponent({
  name: 'OrInput',
  components: {
    OrError: script$1,
    OrHint: script$2,
    OrIcon: script$3,
    OrIconButton: script$4,
    OrInputBox: script$5,
    OrLabel: script$6,
    OrLoader: script$7
  },
  model: {
    prop: 'modelValue',
    event: 'update:modelValue'
  },
  props: {
    modelValue: {
      type: undefined,
      default: undefined
    },
    name: {
      type: String,
      default: undefined
    },
    type: {
      type: String,
      default: () => InputType.Text
    },
    autocomplete: {
      type: String,
      default: 'off'
    },
    variant: {
      type: String,
      default: undefined
    },
    size: {
      type: String,
      default: undefined
    },
    label: {
      type: String,
      default: undefined
    },
    placeholder: {
      type: String,
      default: undefined
    },
    hint: {
      type: String,
      default: undefined
    },
    error: {
      type: [String, Boolean],
      default: undefined
    },
    loading: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'input', 'keydown', 'keyup', 'focus', 'blur'],
  expose: ['root', 'clear', 'focus', 'blur'],
  setup(props, context) {
    const attributes = reactive({
      id: useIdAttribute(),
      ...useControlAttributes(),
      ...useValidationAttributes()
    });
    // Refs & Styles
    const root = ref();
    const rootStyles = computed(() => ['or-input-v3', ...Input, ...(props.variant === InputBoxVariant.Embedded ? ['grow'] : [])]);
    const control = ref();
    const controlStyles = computed(() => [...InputControl]);
    // State
    const model = useProxyModelValue(toRef(props, 'modelValue'), context.emit);
    const disclosed = ref(false);
    const length = computed(() => {
      var _a, _b;
      return (_b = (_a = model.value) === null || _a === void 0 ? void 0 : _a.toString().length) !== null && _b !== void 0 ? _b : 0;
    });
    const maxLength = computed(() => {
      return attributes.maxlength;
    });
    // Methods
    function clear() {
      model.value = undefined;
    }
    function focus() {
      var _a;
      (_a = control.value) === null || _a === void 0 ? void 0 : _a.focus();
    }
    function blur() {
      var _a;
      (_a = control.value) === null || _a === void 0 ? void 0 : _a.blur();
    }
    // Helpers
    const {
      isMobile
    } = useResponsive();
    return {
      attributes,
      root,
      rootStyles,
      control,
      controlStyles,
      model,
      disclosed,
      length,
      maxLength,
      clear,
      focus,
      blur,
      isMobile
    };
  }
});

const _hoisted_1 = ["name", "type", "autocomplete", "placeholder", "required", "readonly", "disabled"];
function render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_OrLabel = resolveComponent("OrLabel");
  const _component_OrLoader = resolveComponent("OrLoader");
  const _component_OrIconButton = resolveComponent("OrIconButton");
  const _component_OrIcon = resolveComponent("OrIcon");
  const _component_OrInputBox = resolveComponent("OrInputBox");
  const _component_OrHint = resolveComponent("OrHint");
  const _component_OrError = resolveComponent("OrError");
  return openBlock(), createElementBlock("div", {
    ref: 'root',
    class: normalizeClass(_ctx.rootStyles)
  }, [_ctx.$slots.label || _ctx.$slots.addon || _ctx.label ? (openBlock(), createBlock(_component_OrLabel, {
    key: 0,
    "control-id": _ctx.attributes.id,
    variant: 'input',
    required: _ctx.required,
    disabled: _ctx.disabled
  }, {
    addon: withCtx(() => [renderSlot(_ctx.$slots, "addon")]),
    default: withCtx(() => [renderSlot(_ctx.$slots, "label", {}, () => [createTextVNode(toDisplayString(_ctx.label), 1 /* TEXT */)])]),
    _: 3 /* FORWARDED */
  }, 8 /* PROPS */, ["control-id", "required", "disabled"])) : createCommentVNode("v-if", true), createVNode(_component_OrInputBox, {
    variant: _ctx.variant,
    size: _ctx.size,
    invalid: !!_ctx.error,
    readonly: _ctx.readonly || _ctx.loading,
    disabled: _ctx.disabled
  }, createSlots({
    default: withCtx(() => [withDirectives(createElementVNode("input", mergeProps({
      ref: 'control'
    }, _ctx.attributes, {
      "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => _ctx.model = $event),
      class: _ctx.controlStyles,
      name: _ctx.name,
      type: _ctx.type === 'password' && _ctx.disclosed ? 'text' : _ctx.type,
      autocomplete: _ctx.autocomplete,
      placeholder: _ctx.placeholder,
      required: _ctx.required,
      readonly: _ctx.readonly || _ctx.loading,
      disabled: _ctx.disabled,
      onInput: _cache[1] || (_cache[1] = $event => _ctx.$emit('input', $event)),
      onKeydown: _cache[2] || (_cache[2] = $event => _ctx.$emit('keydown', $event)),
      onKeyup: _cache[3] || (_cache[3] = $event => _ctx.$emit('keyup', $event)),
      onFocus: _cache[4] || (_cache[4] = $event => _ctx.$emit('focus', $event)),
      onBlur: _cache[5] || (_cache[5] = $event => _ctx.$emit('blur', $event))
    }), null, 16 /* FULL_PROPS */, _hoisted_1), [[vModelDynamic, _ctx.model, void 0, {
      trim: true
    }]])]),
    _: 2 /* DYNAMIC */
  }, [_ctx.loading || _ctx.type === 'password' || _ctx.type === 'search' ? {
    name: "addon",
    fn: withCtx(() => [_ctx.loading ? (openBlock(), createBlock(_component_OrLoader, {
      key: 0,
      variant: 'circular',
      color: _ctx.error ? 'danger' : 'primary',
      size: _ctx.isMobile ? 'm' : 's'
    }, null, 8 /* PROPS */, ["color", "size"])) : (openBlock(), createElementBlock(Fragment, {
      key: 1
    }, [_ctx.type === 'password' ? (openBlock(), createBlock(_component_OrIconButton, {
      key: 0,
      icon: {
        icon: _ctx.disclosed ? 'visibility' : 'visibility_off',
        variant: 'filled'
      },
      color: 'inherit',
      disabled: _ctx.disabled,
      onClick: _cache[6] || (_cache[6] = withModifiers($event => _ctx.disclosed = !_ctx.disclosed, ["stop"]))
    }, null, 8 /* PROPS */, ["icon", "disabled"])) : createCommentVNode("v-if", true), _ctx.type === 'search' ? (openBlock(), createElementBlock(Fragment, {
      key: 1
    }, [_ctx.modelValue && !_ctx.readonly ? (openBlock(), createBlock(_component_OrIconButton, {
      key: 0,
      icon: 'close',
      color: 'inherit',
      disabled: _ctx.disabled,
      onClick: _cache[7] || (_cache[7] = withModifiers($event => _ctx.clear(), ["stop"]))
    }, null, 8 /* PROPS */, ["disabled"])) : (openBlock(), createBlock(_component_OrIcon, {
      key: 1,
      icon: 'search',
      variant: 'outlined'
    }))], 64 /* STABLE_FRAGMENT */)) : createCommentVNode("v-if", true)], 64 /* STABLE_FRAGMENT */))]),
    key: "0"
  } : undefined]), 1032 /* PROPS, DYNAMIC_SLOTS */, ["variant", "size", "invalid", "readonly", "disabled"]), _ctx.$slots.hint || _ctx.hint || _ctx.maxLength ? withDirectives((openBlock(), createBlock(_component_OrHint, {
    key: 1,
    disabled: _ctx.disabled
  }, createSlots({
    default: withCtx(() => [renderSlot(_ctx.$slots, "hint", {}, () => [createTextVNode(toDisplayString(_ctx.hint), 1 /* TEXT */)])]),
    _: 2 /* DYNAMIC */
  }, [_ctx.maxLength ? {
    name: "addon",
    fn: withCtx(() => [createTextVNode(toDisplayString(_ctx.length) + " / " + toDisplayString(_ctx.maxLength), 1 /* TEXT */)]),
    key: "0"
  } : undefined]), 1032 /* PROPS, DYNAMIC_SLOTS */, ["disabled"])), [[vShow, !_ctx.error]]) : createCommentVNode("v-if", true), withDirectives(createVNode(_component_OrError, {
    disabled: _ctx.disabled
  }, createSlots({
    default: withCtx(() => [createTextVNode(toDisplayString(_ctx.error) + " ", 1 /* TEXT */)]),
    _: 2 /* DYNAMIC */
  }, [_ctx.maxLength ? {
    name: "addon",
    fn: withCtx(() => [createTextVNode(toDisplayString(_ctx.length) + " / " + toDisplayString(_ctx.maxLength), 1 /* TEXT */)]),
    key: "0"
  } : undefined]), 1032 /* PROPS, DYNAMIC_SLOTS */, ["disabled"]), [[vShow, typeof _ctx.error === 'string' && _ctx.error.length > 0]])], 2 /* CLASS */);
}

var css_248z = "\ninput[data-v-78975fa2] {\n  min-width: 0;\n  background: none;\n  outline: none;\n}\ninput[type=\"search\"][data-v-78975fa2]::-webkit-search-cancel-button {\n  display: none;\n}\n";
styleInject(css_248z);

script.render = render;
script.__scopeId = "data-v-78975fa2";
script.__file = "src/components/or-input-v3/OrInput.vue";

export { InputType as I, script as s };
